import SegmentsGetter from "./SegmentsGetter";
import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import AllAggregatesGettersRolluper from "./AggregatesGetter/AllAggregatesGettersRolluper";
import {
  useForceRerenderWhenFocusRangeChanges,
  useFocusRange,
  useFilteredProcesses,
  useVisibleProcesses,
  useProcessesInVisibleGroups,
  useExtraTimelineProcessesOnGridPage,
  useTrendsPageFilterState,
  useGetPageFromPath,
} from "../utils/customHooks/genericHooks";
import AllVisibleRolluper from "./AllVisibleRolluper";
import { useShowMachineSwitcherDropdown } from "components/Nav/topBarUtils";
import { isEqual, uniqBy } from "lodash";
import { MAX_TIMELINES_BEFORE_HIDE_INITIALLY } from "constants/constants";
import { useInterval } from "react-use";
import { useDispatch } from "react-redux";
import {
  UPDATE_MANY_ROLLUPS_FOR_PROCESSES,
  addManyNewSegments,
} from "providers/GlobalStateProvider";
import {
  Process,
  ProductionSegmentType,
  RollupWithTagsAndCountsAndRange,
} from "types/types";
import {
  useForceRerenderWhenRetrotagChanges,
  useForceRerenderWhenWorkScheduleChanges,
} from "components/Dashboard/Timeline/RetrotagPopper/retrotagQueryHooks";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useShouldFetchSegments } from "utils/customHooks/whichServerHooks";
import { useForceRerenderOnIntervalIfCurrentRange } from "components/DataGetter/useForceRerenderOnIntervalIfCurrentRange";
import SegmentsGetterStaticPastDataOnly from "./SegmentsGetterStaticPastDataOnly";

const AggregatesAndSegmentsGettersRollupers = () => {
  const focusRange = useFocusRange();
  const visibleProcesses = useVisibleProcesses();

  return !focusRange || visibleProcesses.length === 0 ? null : (
    <>
      <SegmentsGetters />
      <AggregatesGetters />
    </>
  );
};

/** which processes need segments fetched */
const useSegmentsProcesses = () => {
  const visibleProcesses = useVisibleProcesses();
  const processesInVisibleGroups = useProcessesInVisibleGroups();
  const showMachineSwitcherDropdown = useShowMachineSwitcherDropdown();
  const { isTrendsPage } = useGetPageFromPath();
  const { isTrendsPageFilteredByProcess, trendsPageFilterState } =
    useTrendsPageFilterState();
  const processesOnPage = useMemo(
    () =>
      isTrendsPage && isTrendsPageFilteredByProcess
        ? [trendsPageFilterState.process]
        : showMachineSwitcherDropdown
          ? processesInVisibleGroups
          : visibleProcesses,
    [
      isTrendsPage,
      isTrendsPageFilteredByProcess,
      trendsPageFilterState.process,
      showMachineSwitcherDropdown,
      processesInVisibleGroups,
      visibleProcesses,
    ]
  ); // need processes in visibleGroups for pages that use MachineSwitcherDropdown

  // when we visit hublet page from timeline/tiles page, processesOnPage goes like [a,b,c] -> [b]
  // to avoid refetching segments when switching from process tiles page to hublet page, ignore the case where there's only 1 process on the page
  const [processesToFetch, setProcessesToFetch] = useState(processesOnPage);
  useEffect(() => {
    if (
      processesOnPage.length === 1 &&
      processesToFetch.includes(processesOnPage[0])
    ) {
      return;
    }
    if (!isEqual(processesOnPage, processesToFetch)) {
      setProcessesToFetch(processesOnPage);
    }
  }, [processesOnPage, processesToFetch]);

  const extraProcessesToFetchOnGridPage = useExtraTimelineProcessesOnGridPage();

  return useMemo(() => {
    return uniqBy(
      [...processesToFetch, ...extraProcessesToFetchOnGridPage],
      p => p.id
    );
  }, [processesToFetch, extraProcessesToFetchOnGridPage]);
};

function SegmentsGetters() {
  const shouldFetchSegments = useShouldFetchSegments();

  const segmentsProcesses = useSegmentsProcesses();

  const dispatchInterval =
    segmentsProcesses.length > MAX_TIMELINES_BEFORE_HIDE_INITIALLY
      ? 3000
      : 1000;

  /* to improve render speed of pages with many processes,
  store the received segments locally, and batch-dispatch to the store on an interval */
  const queuedSegmentsRef = useRef(
    [] as { processId: string; segments: ProductionSegmentType[] }[]
  );
  const dispatch = useDispatch();
  const [, startTransition] = useTransition();

  useInterval(() => {
    if (queuedSegmentsRef.current.length > 0) {
      startTransition(() => {
        dispatch(addManyNewSegments(queuedSegmentsRef.current));
      });
    }
    queuedSegmentsRef.current = [];
  }, dispatchInterval);
  const rerenderKeyRetrotag = useForceRerenderWhenRetrotagChanges();
  return shouldFetchSegments ? (
    <>
      {segmentsProcesses.map(process => (
        <SegmentsGetterOrSegmentsGetterStatic
          key={`segments-getter-${process.id}-${rerenderKeyRetrotag}`}
          {...{ process, queuedSegmentsRef }}
        />
      ))}
    </>
  ) : null;
}

/** try using SegmentsGetterStatic; then if Askr returns an error for that process, switch that process back to original SegmentsGetter */
function SegmentsGetterOrSegmentsGetterStatic({
  process,
  queuedSegmentsRef,
}: {
  process: Process;
  queuedSegmentsRef: React.MutableRefObject<
    { processId: string; segments: ProductionSegmentType[] }[]
  >;
}) {
  const focusRange = useFocusRange();
  const [error, setError] = useState<Error | null>(null);
  // reset error to allow trying again whenever focusRange changes (otherwise it sticks until page change i.e. when AggregatesAndSegmentsGettersRollupers re-renders when visibpleProcesses.length===0)
  useEffect(() => {
    setError(null);
  }, [focusRange]);
  const isPastDataOnly = focusRange && focusRange.lt < Date.now();

  return isPastDataOnly && !error ? (
    <SegmentsGetterStaticPastDataOnly
      process={process}
      queuedSegmentsRef={queuedSegmentsRef}
      setError={setError}
    />
  ) : (
    <SegmentsGetter process={process} queuedSegmentsRef={queuedSegmentsRef} />
  );
}

/** which processes need aggregates fetched */
const useAggregatesProcesses = () => {
  const visibleProcesses = useVisibleProcesses();
  const filteredProcesses = useFilteredProcesses();
  const filteredProcessIds = filteredProcesses.map(p => p.id);
  const processesInVisibleGroups = useProcessesInVisibleGroups();
  const processesToFetch = useShowMachineSwitcherDropdown()
    ? processesInVisibleGroups
    : visibleProcesses; // need processes in visibleGroups for pages that use MachineSwitcherDropdown

  const extraProcessesToFetchOnGridPage = useExtraTimelineProcessesOnGridPage();

  return useMemo(
    () =>
      uniqBy(
        [...processesToFetch, ...extraProcessesToFetchOnGridPage].filter(p =>
          filteredProcessIds.includes(p.id)
        ),
        p => p.id
      ),
    [processesToFetch, extraProcessesToFetchOnGridPage, filteredProcessIds]
  );
};

const AggregatesGetters = () => {
  const rerenderKeyFocusRange = useForceRerenderWhenFocusRangeChanges();
  const rerenderKeyWorkSchedule = useForceRerenderWhenWorkScheduleChanges();
  const rerenderKeyRetrotag = useForceRerenderWhenRetrotagChanges();
  const rerenderOnIntervalKey = useForceRerenderOnIntervalIfCurrentRange();

  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const [
    {
      general: { hideProducingLosses },
    },
  ] = useFlightConfig();
  const shouldFetchAggregates = !isMidgardDDAEnabled || hideProducingLosses;

  const aggregatesProcesses = useAggregatesProcesses();

  // dispatch on interval to improve render speed of pages with many processes
  // dispatching the aggregates to the store causes a major re-render of many components.
  // so, to stabilize the render, we'll only dispatch once, when all the data is ready.
  const processAggregatesRef = useRef<{
    aggregatesObj: {
      [processId: string]: RollupWithTagsAndCountsAndRange;
    };
    lastUpdate: number;
  }>({
    lastUpdate: Date.now(),
    aggregatesObj: {},
  });
  // empty the bars whenever time range changes (these bars are now stale)
  const focusRange = useFocusRange();
  useEffect(() => {
    processAggregatesRef.current = {
      aggregatesObj: {},
      lastUpdate: Date.now(),
    };
  }, [focusRange]);
  const dispatch = useDispatch();
  const [, startTransition] = useTransition();
  useInterval(() => {
    // check on an interval if we're ready to dispatch yet
    const numAggregates = Object.keys(
      processAggregatesRef.current.aggregatesObj
    ).length;
    const allDataPresent = numAggregates === aggregatesProcesses.length;
    const isNewDataReady = numAggregates > 0;
    const timeElapsed = Date.now() - processAggregatesRef.current.lastUpdate;
    const ready = allDataPresent || (isNewDataReady && timeElapsed > 2 * 1000);
    if (ready) {
      startTransition(() => {
        dispatch({
          type: UPDATE_MANY_ROLLUPS_FOR_PROCESSES,
          payload: processAggregatesRef.current.aggregatesObj,
        });
        processAggregatesRef.current = {
          ...processAggregatesRef.current,
          aggregatesObj: {},
          lastUpdate: Date.now(),
        };
      });
    }
  }, 2 * 1000);

  return shouldFetchAggregates ? (
    <>
      {aggregatesProcesses.map(process => (
        <AllAggregatesGettersRolluper
          key={`segments-rolluper-${process.id}-${rerenderKeyFocusRange}-${rerenderKeyRetrotag}-${rerenderKeyWorkSchedule}-${rerenderOnIntervalKey}`}
          process={process}
          queueRollupForDispatch={rollup => {
            processAggregatesRef.current = {
              ...processAggregatesRef.current,
              aggregatesObj: {
                ...processAggregatesRef.current.aggregatesObj,
                [process.id]: rollup,
              },
            };
          }}
        />
      ))}
    </>
  ) : null;
};

export default function AllAggregatesAndSegmentsGettersRollupers() {
  return (
    <>
      <AggregatesAndSegmentsGettersRollupers />
      <AllVisibleRolluper />
    </>
  );
}
